// src/CreatorUser/CreatorPages/Dashboard/Bio/CreatorBioDesktop.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  LinearProgress,
  Fade,
  Slide,
  AppBar,
  Toolbar,
  Container,
  Stack,
  Tooltip,
  ButtonGroup,
  Avatar,
  FormHelperText,
  Badge,
  Fab,
  Tabs,
  Tab,
  Snackbar,
  InputAdornment,
  CircularProgress,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DeleteOutline,
  ExpandMore,
  Add,
  Save,
  Public,
  PublicOff,
  QrCode,
  DragIndicator,
  Image,
  Link,
  Visibility,
  Business,
  Person,
  Cancel,
  ContactPage,
  ViewModule,
  ViewQuilt,
  Info,
  SwapHoriz,
  Email,
  Phone,
  AccessTime,
  RefreshOutlined,
  Star,
  StarBorder,
  Animation,
  Timeline,
  Link as LinkIcon,
  HelpOutline,
  Upload,
  CloudUpload,
  PhotoCamera,
  Videocam,
  AccountCircle,
  Settings,
  Pages,
  Palette,
  CheckCircle,
  Error,
  Warning,
  Close,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import API from "../../../../API";
import AlertDialog from "../../../../Components/AlertDialog";
import useAlertDialog from "../../../../Components/useAlertDialog";
import { EnhancedBioPagePreview } from "./previewhelper";
import HelpWizard from "./helpWizard";
import { styled } from "@mui/material/styles";
import ColorPickerGrid from "./colorpickergrid";
import UnifiedProfileSettings from "../../../../GlobalPages/globalUser/UnitedProfileSettins";
import UploadFileDialog from "../../../../Components/tools/uploadfile";
import { validateSlug, suggestAlternatives, isProhibitedSlug, getProhibitedSlugs } from "./prohibitedSlugs";

// ************************************************************
// STYLED COMPONENTS
// ************************************************************
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)'
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden'
}));

const HelpFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
  }
}));

const MediaUploadZone = styled(Box)(({ theme, hasMedia }) => ({
  border: hasMedia ? `2px solid ${theme.palette.success.main}` : `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  textAlign: 'center',
  backgroundColor: hasMedia ? theme.palette.success.light + '20' : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  }
}));

const PageSelectionChip = styled(Chip)(({ theme, selected }) => ({
  height: 48,
  borderRadius: theme.spacing(3),
  fontSize: '0.875rem',
  fontWeight: 600,
  border: selected 
    ? `2px solid ${theme.palette.primary.main}` 
    : `2px solid ${theme.palette.divider}`,
  backgroundColor: selected 
    ? theme.palette.primary.main 
    : 'transparent',
  color: selected 
    ? theme.palette.primary.contrastText 
    : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected 
      ? theme.palette.primary.dark 
      : theme.palette.action.hover,
  },
  transition: 'all 0.2s ease'
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 64,
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  }
}));

// Enhanced tooltip wrapper component
const TooltipWrapper = ({ title, children, placement = "top" }) => (
  <Tooltip title={title} placement={placement} arrow>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {children}
    </Box>
  </Tooltip>
);

// Tab Panel Component
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// ************************************************************
// MAIN DESKTOP COMPONENT
// ************************************************************
export default function CreatorBioDesktop() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const theme = useTheme();

  // Main tab state (Profile, Pages, Items)
  const [mainTab, setMainTab] = useState(0);

  // External Transfers state
  const [transferStatusFilter, setTransferStatusFilter] = useState('pending');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [editForm, setEditForm] = useState({ expires_in_days: '', recipient_email: '', recipient_phone: '' });

  // ---------------------
  // QUERIES & MUTATIONS
  // ---------------------
  const {
    data: pages,
    isLoading: isPagesLoading,
    error: pagesError,
  } = useQuery(["bioPages"], API.bio.list, {
    refetchOnWindowFocus: false,
  });

  const {
    data: availableContacts,
    isLoading: isContactsLoading,
  } = useQuery(["bioContacts"], API.bio.getAvailableContacts, {
    refetchOnWindowFocus: false,
  });

  // External transfers query
  const {
    data: transfersData,
    isLoading: isTransfersLoading,
    refetch: refetchTransfers,
  } = useQuery(
    ["externalTransfers", transferStatusFilter, includeExpired],
    () => API.balanceLedger.listExternalTransfers({
      status: transferStatusFilter,
      include_expired: includeExpired,
      limit: 50,
      offset: 0,
    }),
    {
      refetchOnWindowFocus: false,
      enabled: mainTab === 3,
    }
  );

  const cancelTransferMutation = useMutation(
    (transferId) => API.balanceLedger.cancelExternalTransfer(transferId),
    {
      onSuccess: () => {
        setCancelDialogOpen(false);
        setSelectedTransfer(null);
        queryClient.invalidateQueries(["externalTransfers"]);
        setSnackbarMessage("Transfer cancelled and funds refunded successfully");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      },
      onError: (error) => {
        setSnackbarMessage(error?.response?.data?.error || "Failed to cancel transfer");
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      },
    }
  );

  const updateTransferMutation = useMutation(
    ({ id, data }) => API.balanceLedger.updateExternalTransfer(id, data),
    {
      onSuccess: (data) => {
        setEditDialogOpen(false);
        setSelectedTransfer(null);
        setEditForm({ expires_in_days: '', recipient_email: '', recipient_phone: '' });
        queryClient.invalidateQueries(["externalTransfers"]);
        const messages = [];
        if (data?.resend_claim_to_email) messages.push(`Claim email sent to ${data.resend_claim_to_email}`);
        if (data?.resend_claim_to_phone) messages.push(`Claim SMS sent to ${data.resend_claim_to_phone}`);
        setSnackbarMessage(messages.length > 0 ? `Transfer updated. ${messages.join('. ')}` : "Transfer updated successfully");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      },
      onError: (error) => {
        setSnackbarMessage(error?.response?.data?.error || "Failed to update transfer");
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      },
    }
  );

  // ---------------------
  // LOCAL STATE
  // ---------------------
  const [isNewPage, setIsNewPage] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(-1);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [pageType, setPageType] = useState("BUSINESS");
  const [viewType, setViewType] = useState("simple");
  const [linkedContactId, setLinkedContactId] = useState(null);
  const [linkUserPage, setLinkUserPage] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [animationType, setAnimationType] = useState("FADE_IN");
  const [isPublic, setIsPublic] = useState(true);
  const [qrEnabled, setQrEnabled] = useState(false);
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [themeObj, setThemeObj] = useState({
    backgroundColor: "#ffffff",
    foregroundColor: "#000000",
  });
  const [items, setItems] = useState([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isBackgroundPickerOpen, setIsBackgroundPickerOpen] = useState(false);
  const [isForegroundPickerOpen, setIsForegroundPickerOpen] = useState(false);
  const [isHelpWizardOpen, setIsHelpWizardOpen] = useState(false);
  const [profileUploadDialogOpen, setProfileUploadDialogOpen] = useState(false);
  const [profilePhotoCallback, setProfilePhotoCallback] = useState(null);

  // Slug validation state
  const [slugValidation, setSlugValidation] = useState({ isValid: true, message: '' });
  const [slugSuggestions, setSlugSuggestions] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // ---------------------
  // MUTATIONS
  // ---------------------
  const createMutation = useMutation(
    (newPageData) => API.bio.create(newPageData),
    {
      onSuccess: () => {
        openDialog("Success", "Bio page created successfully! 🎉", () => {
          closeDialog();
          queryClient.invalidateQueries(["bioPages"]);
          setMainTab(1); // Switch to pages tab
        });
      },
      onError: (error) => {
        openDialog(
          "Error",
          error?.response?.data?.error || error.message,
          closeDialog,
          closeDialog
        );
      },
    }
  );

  const updateMutation = useMutation(
    ({ oldSlug, updated }) => API.bio.update(oldSlug, updated),
    {
      onSuccess: () => {
        openDialog("Success", "Bio page updated successfully! ✨", () => {
          closeDialog();
          queryClient.invalidateQueries(["bioPages"]);
        });
      },
      onError: (error) => {
        openDialog(
          "Error",
          error?.response?.data?.error || error.message,
          closeDialog,
          closeDialog
        );
      },
    }
  );

  // ---------------------
  // EFFECTS
  // ---------------------
  
  // Set default selection to profile page if it exists (only when switching to pages tab)
  useEffect(() => {
    if (mainTab === 1 && !isPagesLoading && pages && pages.length > 0 && selectedPageIndex === -1) {
      const profilePageIndex = pages.findIndex((page) => page.page_type === "PROFILE");
      if (profilePageIndex !== -1) {
        handleSelectPage(profilePageIndex);
      } else {
        handleSelectPage(0);
      }
    }
  }, [mainTab, isPagesLoading, pages, selectedPageIndex]);

  // Real-time slug validation
  useEffect(() => {
    if (slug && isNewPage) {
      const validation = validateSlug(slug);
      setSlugValidation(validation);
      
      if (!validation.isValid && isProhibitedSlug(slug)) {
        const suggestions = suggestAlternatives(slug);
        setSlugSuggestions(suggestions);
        
        // Show snackbar for prohibited slugs
        setSnackbarMessage(`"${slug}" is reserved. Try one of the suggestions below!`);
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      } else {
        setSlugSuggestions([]);
      }
    } else if (!slug && isNewPage) {
      setSlugValidation({ isValid: false, message: '' });
      setSlugSuggestions([]);
    }
  }, [slug, isNewPage]);

  // ---------------------
  // LOADING & ERROR STATES
  // ---------------------
  if (isPagesLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Fade in timeout={600}>
          <Box textAlign="center" py={8}>
            <Business sx={{ fontSize: 64, color: 'primary.main', mb: 3 }} />
            <Typography variant="h4" gutterBottom color="primary">
              Loading Your Profile & Bio Pages
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Preparing your professional management interface...
            </Typography>
            <LinearProgress sx={{ 
              width: 400, 
              mx: 'auto',
              height: 8, 
              borderRadius: 4,
              bgcolor: 'grey.100'
            }} />
          </Box>
        </Fade>
      </Container>
    );
  }

  if (pagesError) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>Error Loading Bio Pages</Typography>
          <Typography>
            {pagesError?.response?.data?.error || pagesError.message}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => setIsHelpWizardOpen(true)}
            startIcon={<HelpOutline />}
            sx={{ mt: 2 }}
          >
            Get Help
          </Button>
        </Alert>
      </Container>
    );
  }

  // ---------------------
  // EVENT HANDLERS
  // ---------------------
  function handleSelectPage(index) {
    if (index === -1) {
      setIsNewPage(true);
      setSelectedPageIndex(-1);
      setSlug("");
      setTitle("");
      setDescription("");
      setPageType("BUSINESS");
      setViewType("simple");
      setLinkedContactId(null);
      setLinkUserPage(false);
      setAnimationsEnabled(true);
      setAnimationType("FADE_IN");
      setIsPublic(true);
      setQrEnabled(false);
      setHeaderMediaUrl("");
      setThemeObj({ backgroundColor: "#ffffff", foregroundColor: "#000000" });
      setItems([]);
      setSlugValidation({ isValid: false, message: '' });
      setSlugSuggestions([]);
    } else {
      setIsNewPage(false);
      setSelectedPageIndex(index);
      const page = pages[index];
      setSlug(page.slug);
      setTitle(page.title || "");
      setDescription(page.description || "");
      setPageType(page.page_type || "BUSINESS");
      setViewType(page.view_type || "simple");
      setLinkedContactId(page.linked_contact_id);
      setLinkUserPage(page.link_user_page || false);
      setAnimationsEnabled(page.animations_enabled !== false);
      setAnimationType(page.animation_type || "FADE_IN");
      setIsPublic(page.is_public);
      setThemeObj(page.theme || { backgroundColor: "#ffffff", foregroundColor: "#000000" });
      setQrEnabled(page.qr_enabled);
      setHeaderMediaUrl(page.header_media_url || "");
      setItems(page.items || []);
      setSlugValidation({ isValid: true, message: '' });
      setSlugSuggestions([]);
    }
  }

  function handleAddItem() {
    const newItem = {
      component_type: "LINK",
      label: "New Link",
      url: "https://example.com",
      media_url: "",
      size: "MEDIUM",
      is_enabled: true,
    };
    setItems((prev) => [...prev, newItem]);
  }

  function handleRemoveItem(idx) {
    const updated = [...items];
    updated.splice(idx, 1);
    setItems(updated);
  }

  function handleUpdateItemField(idx, field, value) {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  }

  function handleSave() {
    // Validate slug for new pages
    if (isNewPage) {
      const validation = validateSlug(slug);
      if (!validation.isValid) {
        setSnackbarMessage(validation.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
    }

    if (!slug) {
      setSnackbarMessage("A slug is required to create your bio page!");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    const payload = {
      slug,
      title,
      description,
      page_type: pageType,
      view_type: viewType,
      linked_contact_id: linkedContactId,
      link_user_page: pageType === "PROFILE" ? linkUserPage : false,
      animations_enabled: animationsEnabled,
      animation_type: animationType,
      is_public: isPublic,
      qr_enabled: qrEnabled,
      theme: themeObj,
      header_media_url: headerMediaUrl,
      items: items.map((item, idx) => ({ ...item, order_index: idx })),
    };
    
    if (isNewPage) {
      createMutation.mutate(payload);
    } else {
      const oldSlug = pages[selectedPageIndex].slug;
      updateMutation.mutate({ oldSlug, updated: payload });
    }
  }

  function onDragEnd(result) {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    setItems((prev) => {
      const updatedItems = [...prev];
      const [moved] = updatedItems.splice(source.index, 1);
      updatedItems.splice(destination.index, 0, moved);
      return updatedItems;
    });
  }

  const selectedPage = selectedPageIndex >= 0 ? pages[selectedPageIndex] : null;
  const linkedContact = selectedPage?.linked_contact;

  // ---------------------
  // RENDER HELPERS
  // ---------------------
  const renderMediaUploadZone = (mediaUrl, onUpload, title, description) => (
    <MediaUploadZone
      hasMedia={!!mediaUrl}
      onClick={onUpload}
    >
      {mediaUrl ? (
        <Box>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
            <Image color="success" />
            <Typography variant="subtitle1" color="success.main" fontWeight={600}>
              Media Added
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click to change media
          </Typography>
          {/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(mediaUrl) ? (
            <Box
              component="img"
              src={mediaUrl}
              alt="Preview"
              sx={{ maxWidth: 120, maxHeight: 80, borderRadius: 1 }}
            />
          ) : (
            <Box
              sx={{
                width: 120,
                height: 80,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                mx: 'auto'
              }}
            >
              <Videocam color="action" />
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <CloudUpload sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      )}
    </MediaUploadZone>
  );

  // ---------------------
  // MAIN RENDER
  // ---------------------
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AlertDialog alertState={dialogState} />
      
      {/* Help FAB */}
      <TooltipWrapper title="Get help with your profile and bio pages">
        <HelpFab
          color="primary"
          onClick={() => setIsHelpWizardOpen(true)}
        >
          <HelpOutline />
        </HelpFab>
      </TooltipWrapper>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
          variant="filled"
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSnackbarOpen(false)}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Header */}
      <StyledPaper elevation={0} sx={{ borderRadius: 0, mb: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ py: 4 }}>
            <Fade in timeout={600}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
                    Profile & Bio Manager
                  </Typography>
                  <Chip
                    icon={<Pages />}
                    label={`${pages?.length || 0} Bio Pages`}
                    color="primary"
                    size="medium"
                    sx={{ fontSize: '1rem', py: 3 }}
                  />
                </Box>
                <Typography variant="body1" color="text.secondary">
                  Manage your professional profile and create engaging bio pages to share your links and content.
                </Typography>
              </Box>
            </Fade>
          </Box>
        </Container>
      </StyledPaper>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <StyledCard sx={{ mb: 3 }}>
          <Tabs
            value={mainTab}
            onChange={(e, newValue) => setMainTab(newValue)}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <StyledTab icon={<AccountCircle />} label="Profile Settings" />
            <StyledTab icon={<Pages />} label="Bio Pages" />
            <StyledTab icon={<Settings />} label="Page Configuration" />
            <StyledTab icon={<SwapHoriz />} label="Manage Transfers" />
          </Tabs>
        </StyledCard>

        {/* Tab 0: Profile Settings */}
        <TabPanel value={mainTab} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8} mx="auto">
              <Fade in timeout={600}>
                <Box>
                  <Box textAlign="center" mb={4}>
                    <Typography variant="h4" gutterBottom fontWeight="bold">
                      Your Profile
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Update your personal or business profile information. This information is used across your bio pages and professional presence.
                    </Typography>
                  </Box>
                  
                  <UnifiedProfileSettings 
                    onUploadPhoto={(callback) => {
                      setProfilePhotoCallback(() => callback);
                      setProfileUploadDialogOpen(true);
                    }}
                    compact={false}
                    showContext={true}
                  />

                  <Box mt={4}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="body2">
                        <strong>Pro Tip:</strong> Your profile information can be automatically linked to your bio pages. 
                        Switch to the "Bio Pages" tab to create or edit your pages.
                      </Typography>
                    </Alert>
                  </Box>

                  <Box mt={3} textAlign="center">
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => setMainTab(1)}
                      endIcon={<Pages />}
                    >
                      Manage Bio Pages
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 1: Bio Pages Overview */}
        <TabPanel value={mainTab} index={1}>
          <Fade in timeout={600}>
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Your Bio Pages
                </Typography>
                <TooltipWrapper title="Learn about page types and creation">
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<Info />}
                    onClick={() => setIsHelpWizardOpen(true)}
                  >
                    Learn More
                  </Button>
                </TooltipWrapper>
              </Box>

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={4}>
                {pages?.map((page, i) => (
                  <TooltipWrapper
                    key={page.id}
                    title={`${page.page_type === 'PROFILE' ? 'Profile' : 'Business'} page: /${page.slug}`}
                  >
                    <PageSelectionChip
                      selected={selectedPageIndex === i}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          {page.page_type === 'PROFILE' && <Star fontSize="small" />}
                          <Typography variant="body2" fontWeight="inherit">
                            {page.slug}
                          </Typography>
                          <Badge
                            badgeContent={page.items?.length || 0}
                            color="secondary"
                            sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: 16, height: 16 } }}
                          >
                            <Box />
                          </Badge>
                        </Box>
                      }
                      onClick={() => {
                        handleSelectPage(i);
                        setMainTab(2);
                      }}
                      clickable
                    />
                  </TooltipWrapper>
                ))}
                <TooltipWrapper title="Create a new bio page">
                  <PageSelectionChip
                    selected={false}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Add fontSize="small" />
                        <Typography variant="body2" fontWeight="inherit">
                          Create New
                        </Typography>
                      </Box>
                    }
                    onClick={() => {
                      handleSelectPage(-1);
                      setMainTab(2);
                    }}
                    clickable
                  />
                </TooltipWrapper>
              </Stack>

              {pages && pages.length > 0 ? (
                <Grid container spacing={3}>
                  {pages.map((page, i) => (
                    <Grid item xs={12} md={6} lg={4} key={page.id}>
                      <StyledCard>
                        <CardContent>
                          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {page.page_type === 'PROFILE' ? (
                                <Star color="success" />
                              ) : (
                                <Business color="primary" />
                              )}
                              <Typography variant="h6" fontWeight="bold">
                                {page.slug}
                              </Typography>
                            </Box>
                            <Chip 
                              label={page.page_type === 'PROFILE' ? 'Profile' : 'Business'}
                              color={page.page_type === 'PROFILE' ? 'success' : 'primary'}
                              size="small"
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {page.title || 'No title set'}
                          </Typography>

                          <Divider sx={{ my: 2 }} />

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                              icon={<Link />}
                              label={`${page.items?.length || 0} items`}
                              size="small"
                              variant="outlined"
                            />
                            {page.is_public && (
                              <Chip
                                icon={<Public />}
                                label="Public"
                                size="small"
                                variant="outlined"
                                color="info"
                              />
                            )}
                            {page.qr_enabled && (
                              <Chip
                                icon={<QrCode />}
                                label="QR"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            onClick={() => {
                              handleSelectPage(i);
                              setMainTab(2);
                            }}
                          >
                            Edit Page
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              handleSelectPage(i);
                              setIsPreviewOpen(true);
                            }}
                            startIcon={<Visibility />}
                          >
                            Preview
                          </Button>
                        </CardActions>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box 
                  textAlign="center" 
                  py={8}
                  sx={{ 
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 3,
                    bgcolor: 'grey.50'
                  }}
                >
                  <Business sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No Bio Pages Yet
                  </Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ mb: 4 }}>
                    Create your first bio page to share your links and content with the world
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Add />}
                    onClick={() => {
                      handleSelectPage(-1);
                      setMainTab(2);
                    }}
                  >
                    Create Your First Bio Page
                  </Button>
                </Box>
              )}
            </Box>
          </Fade>
        </TabPanel>

        {/* Tab 2: Page Configuration & Items */}
        <TabPanel value={mainTab} index={2}>
          {selectedPageIndex === -1 && !isNewPage ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No Page Selected
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 4 }}>
                Please select a page from the Bio Pages tab to configure it
              </Typography>
              <Button
                variant="contained"
                onClick={() => setMainTab(1)}
              >
                Go to Bio Pages
              </Button>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Left Panel - Page Configuration */}
              <Grid item xs={12} lg={6}>
                <Stack spacing={3}>
                  {/* Page Details Card */}
                  <StyledCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          {isNewPage ? "Create New Bio Page" : "Edit Bio Page"}
                        </Typography>
                        <Chip 
                          label={isNewPage ? "New" : "Editing"} 
                          color={isNewPage ? "success" : "primary"} 
                          size="small"
                        />
                      </Box>

                      <Stack spacing={3}>
                        {/* Page Type Selection */}
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                              Page Type
                            </Typography>
                            <TooltipWrapper title="Profile pages are special - you can only have one. Business pages are unlimited.">
                              <IconButton size="small">
                                <Info fontSize="small" />
                              </IconButton>
                            </TooltipWrapper>
                          </Box>
                          
                          <Stack direction="row" spacing={2} mb={2}>
                            <TooltipWrapper title="Your main bio page - perfect for social media bios">
                              <Button
                                variant={pageType === "PROFILE" ? "contained" : "outlined"}
                                color="success"
                                startIcon={<Star />}
                                onClick={() => setPageType("PROFILE")}
                                sx={{ flex: 1 }}
                              >
                                Profile Page
                              </Button>
                            </TooltipWrapper>
                            <TooltipWrapper title="For specific projects, businesses, or campaigns">
                              <Button
                                variant={pageType === "BUSINESS" ? "contained" : "outlined"}
                                color="primary"
                                startIcon={<Business />}
                                onClick={() => setPageType("BUSINESS")}
                                sx={{ flex: 1 }}
                              >
                                Business Page
                              </Button>
                            </TooltipWrapper>
                          </Stack>
                          
                          {pageType === "PROFILE" && (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              <Typography variant="body2">
                                <strong>Profile pages</strong> are your primary bio page. You can only have one profile page, 
                                but unlimited business pages.
                              </Typography>
                            </Alert>
                          )}
                        </Box>

                        {/* Basic Information */}
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                              Basic Information
                            </Typography>
                            {isNewPage && (
                              <TooltipWrapper title="Some slugs are reserved for system routes and cannot be used">
                                <IconButton 
                                  size="small"
                                  onClick={() => {
                                    const count = getProhibitedSlugs().length;
                                    setSnackbarMessage(`${count} routes are reserved and cannot be used as slugs. We'll let you know if you try one!`);
                                    setSnackbarSeverity('info');
                                    setSnackbarOpen(true);
                                  }}
                                >
                                  <Info fontSize="small" />
                                </IconButton>
                              </TooltipWrapper>
                            )}
                          </Box>
                          
                          <Stack spacing={2}>
                            <TextField
                              label="Slug (URL Path)"
                              value={slug}
                              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                              fullWidth
                              disabled={!isNewPage}
                              helperText={
                                slug ? (
                                  <Box component="span" display="flex" alignItems="center" gap={0.5}>
                                    {slugValidation.isValid ? (
                                      <>
                                        <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                        <span style={{ color: theme.palette.success.main }}>
                                          ✓ Available at: /{slug}
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <Error sx={{ fontSize: 16, color: 'error.main' }} />
                                        <span style={{ color: theme.palette.error.main }}>
                                          {slugValidation.message}
                                        </span>
                                      </>
                                    )}
                                  </Box>
                                ) : (
                                  'Your page will be available at: /your-slug'
                                )
                              }
                              variant="outlined"
                              error={slug && !slugValidation.isValid}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography color="text.secondary">/</Typography>
                                  </InputAdornment>
                                ),
                                endAdornment: slug && (
                                  <InputAdornment position="end">
                                    {slugValidation.isValid ? (
                                      <CheckCircle color="success" />
                                    ) : (
                                      <Error color="error" />
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {/* Show suggestions if slug is prohibited */}
                            {slugSuggestions.length > 0 && (
                              <Alert severity="info" sx={{ borderRadius: 2 }}>
                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                  💡 Try these alternatives:
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
                                  {slugSuggestions.map((suggestion) => (
                                    <Chip
                                      key={suggestion}
                                      label={suggestion}
                                      size="small"
                                      onClick={() => {
                                        setSlug(suggestion);
                                        setSnackbarMessage(`Updated slug to "${suggestion}"`);
                                        setSnackbarSeverity('success');
                                        setSnackbarOpen(true);
                                      }}
                                      clickable
                                      color="primary"
                                      variant="outlined"
                                      icon={<CheckCircle />}
                                    />
                                  ))}
                                </Stack>
                              </Alert>
                            )}
                            
                            <TextField
                              label="Page Title"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                              fullWidth
                              variant="outlined"
                              placeholder="e.g., John Smith, Creative Director"
                            />
                            
                            <TextField
                              label="Description"
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              fullWidth
                              multiline
                              rows={3}
                              variant="outlined"
                              placeholder="Tell visitors about yourself..."
                            />
                          </Stack>
                        </Box>

                        {/* Header Media */}
                        <Box>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                            Header Media
                          </Typography>
                          {renderMediaUploadZone(
                            headerMediaUrl,
                            () => {
                              setUploadTarget("header");
                              setIsUploadDialogOpen(true);
                            },
                            "Upload Header Media",
                            "Add a banner image or video"
                          )}
                        </Box>

                        {/* Profile Page Linking */}
                        {pageType === "PROFILE" && (
                          <Box>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                              Page Linking
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={linkUserPage}
                                  onChange={(e) => setLinkUserPage(e.target.checked)}
                                  color="primary"
                                />
                              }
                              label="Link to User/Mediakit Page"
                            />
                            <FormHelperText>
                              Adds a button linking to your main user profile
                            </FormHelperText>
                          </Box>
                        )}

                        {/* Contact Linking */}
                        {pageType === "BUSINESS" && availableContacts && availableContacts.length > 0 && (
                          <Box>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                              Contact Linking
                            </Typography>
                            <FormControl fullWidth>
                              <InputLabel>Link a Contact</InputLabel>
                              <Select
                                value={linkedContactId || ""}
                                label="Link a Contact"
                                onChange={(e) => setLinkedContactId(e.target.value || null)}
                              >
                                <MenuItem value="">
                                  <em>No Contact</em>
                                </MenuItem>
                                {availableContacts.map((contact) => (
                                  <MenuItem key={contact.id} value={contact.id}>
                                    {contact.full_name} ({contact.category})
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                Display contact information from your Contact Manager
                              </FormHelperText>
                            </FormControl>
                          </Box>
                        )}

                        {/* View Type */}
                        <Box>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                            Layout Style
                          </Typography>
                          <ButtonGroup fullWidth>
                            <Button
                              variant={viewType === "simple" ? "contained" : "outlined"}
                              onClick={() => setViewType("simple")}
                              startIcon={<ViewModule />}
                            >
                              Simple
                            </Button>
                            <Button
                              variant={viewType === "grid" ? "contained" : "outlined"}
                              onClick={() => setViewType("grid")}
                              startIcon={<ViewQuilt />}
                            >
                              Grid
                            </Button>
                          </ButtonGroup>
                        </Box>

                        {/* Theme Colors */}
                        <Box>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                            Theme Colors
                          </Typography>
                          <Stack direction="row" spacing={2}>
                            <Box sx={{ flex: 1 }}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => setIsBackgroundPickerOpen(true)}
                                startIcon={<Palette />}
                                sx={{ justifyContent: 'flex-start', height: 56 }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: 1,
                                      bgcolor: themeObj.backgroundColor,
                                      border: '2px solid',
                                      borderColor: 'divider'
                                    }}
                                  />
                                  <Typography variant="body2">Background</Typography>
                                </Box>
                              </Button>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => setIsForegroundPickerOpen(true)}
                                startIcon={<Palette />}
                                sx={{ justifyContent: 'flex-start', height: 56 }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: 1,
                                      bgcolor: themeObj.foregroundColor,
                                      border: '2px solid',
                                      borderColor: 'divider'
                                    }}
                                  />
                                  <Typography variant="body2">Text</Typography>
                                </Box>
                              </Button>
                            </Box>
                          </Stack>
                        </Box>

                        {/* Settings */}
                        <Box>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                            Page Settings
                          </Typography>
                          <Stack spacing={2}>
                            <FormControlLabel
                              control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
                              label={
                                <Box display="flex" alignItems="center" gap={1}>
                                  {isPublic ? <Public /> : <PublicOff />}
                                  <Typography>
                                    {isPublic ? "Public Page" : "Private Page"}
                                  </Typography>
                                </Box>
                              }
                            />
                            <FormControlLabel
                              control={<Switch checked={qrEnabled} onChange={(e) => setQrEnabled(e.target.checked)} />}
                              label={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <QrCode />
                                  <Typography>Enable QR Code</Typography>
                                </Box>
                              }
                            />
                            <FormControlLabel
                              control={<Switch checked={animationsEnabled} onChange={(e) => setAnimationsEnabled(e.target.checked)} />}
                              label={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Animation />
                                  <Typography>Enable Animations</Typography>
                                </Box>
                              }
                            />
                            {animationsEnabled && (
                              <FormControl fullWidth size="small">
                                <InputLabel>Animation Type</InputLabel>
                                <Select
                                  value={animationType}
                                  label="Animation Type"
                                  onChange={(e) => setAnimationType(e.target.value)}
                                >
                                  <MenuItem value="FADE_IN">Fade In</MenuItem>
                                  <MenuItem value="SLIDE_UP">Slide Up</MenuItem>
                                  <MenuItem value="ZOOM">Zoom</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          </Stack>
                        </Box>

                        {/* Save Button */}
                        <Button
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={<Save />}
                          onClick={handleSave}
                          disabled={createMutation.isLoading || updateMutation.isLoading || (isNewPage && !slugValidation.isValid)}
                          sx={{ mt: 2, py: 2 }}
                        >
                          {createMutation.isLoading || updateMutation.isLoading
                            ? "Saving..."
                            : isNewPage
                            ? "Create Bio Page"
                            : "Save Changes"}
                        </Button>
                      </Stack>
                    </CardContent>
                  </StyledCard>
                </Stack>
              </Grid>

              {/* Right Panel - Items & Preview */}
              <Grid item xs={12} lg={6}>
                <Stack spacing={3}>
                  {/* Items Management */}
                  <StyledCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Page Items
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={handleAddItem}
                          size="small"
                        >
                          Add Item
                        </Button>
                      </Box>

                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="items">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {items.map((item, idx) => (
                                <Draggable
                                  key={`item-${idx}`}
                                  draggableId={`item-${idx}`}
                                  index={idx}
                                >
                                  {(provided, snapshot) => (
                                    <Box
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      sx={{ mb: 2 }}
                                    >
                                      <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                          <Box {...provided.dragHandleProps} sx={{ mr: 2 }}>
                                            <DragIndicator color="action" />
                                          </Box>
                                          <Link sx={{ mr: 2, color: 'primary.main' }} />
                                          <Typography variant="body1" sx={{ flex: 1 }}>
                                            {item.label || "(Untitled)"}
                                          </Typography>
                                          <Chip label={item.size} size="small" />
                                        </AccordionSummary>
                                        <AccordionDetails>
                                          <Stack spacing={2}>
                                            <TextField
                                              label="Label"
                                              size="small"
                                              value={item.label}
                                              onChange={(e) => handleUpdateItemField(idx, "label", e.target.value)}
                                              fullWidth
                                            />
                                            <TextField
                                              label="URL"
                                              size="small"
                                              value={item.url || ""}
                                              onChange={(e) => handleUpdateItemField(idx, "url", e.target.value)}
                                              fullWidth
                                            />
                                            <Box display="flex" gap={2}>
                                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                                <InputLabel>Size</InputLabel>
                                                <Select
                                                  value={item.size || "MEDIUM"}
                                                  label="Size"
                                                  onChange={(e) => handleUpdateItemField(idx, "size", e.target.value)}
                                                >
                                                  <MenuItem value="SMALL">Small</MenuItem>
                                                  <MenuItem value="MEDIUM">Medium</MenuItem>
                                                  <MenuItem value="LARGE">Large</MenuItem>
                                                </Select>
                                              </FormControl>
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Upload />}
                                                onClick={() => {
                                                  setUploadTarget(idx);
                                                  setIsUploadDialogOpen(true);
                                                }}
                                              >
                                                Media
                                              </Button>
                                              <IconButton
                                                onClick={() => handleRemoveItem(idx)}
                                                color="error"
                                                size="small"
                                              >
                                                <DeleteOutline />
                                              </IconButton>
                                            </Box>
                                          </Stack>
                                        </AccordionDetails>
                                      </Accordion>
                                    </Box>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>

                      {items.length === 0 && (
                        <Box textAlign="center" py={6}>
                          <Link sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            No items yet
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleAddItem}
                            sx={{ mt: 2 }}
                          >
                            Add First Item
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </StyledCard>

                  {/* Live Preview */}
                  <StyledCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Live Preview
                      </Typography>
                      <Box 
                        sx={{ 
                          maxHeight: 600, 
                          overflow: 'auto',
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 2
                        }}
                      >
                        <EnhancedBioPagePreview
                          title={title}
                          slug={slug}
                          description={description}
                          pageType={pageType}
                          viewType={viewType}
                          items={items}
                          isPublic={isPublic}
                          qrEnabled={qrEnabled}
                          animationsEnabled={animationsEnabled}
                          animationType={animationType}
                          linkUserPage={linkUserPage}
                          headerMediaUrl={headerMediaUrl}
                          linkedContact={linkedContact}
                          customtheme={themeObj}
                        />
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Stack>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Tab 3: Manage Transfers */}
        <TabPanel value={mainTab} index={3}>
          <Fade in timeout={600}>
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Manage Transfers
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={transferStatusFilter}
                      label="Status"
                      onChange={(e) => setTransferStatusFilter(e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="claimed">Claimed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                    </Select>
                  </FormControl>
                  {transferStatusFilter === 'pending' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeExpired}
                          onChange={(e) => setIncludeExpired(e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Include Expired</Typography>}
                    />
                  )}
                  <TooltipWrapper title="Refresh transfers">
                    <IconButton onClick={() => refetchTransfers()} size="small">
                      <RefreshOutlined />
                    </IconButton>
                  </TooltipWrapper>
                </Box>
              </Box>

              {isTransfersLoading ? (
                <Box textAlign="center" py={8}>
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading transfers...
                  </Typography>
                </Box>
              ) : transfersData?.transfers && transfersData.transfers.length > 0 ? (
                <>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Showing {transfersData.transfers.length} of {transfersData.total_count || transfersData.transfers.length} transfers
                  </Typography>
                  <Stack spacing={0}>
                    {transfersData.transfers.map((transfer) => {
                      const isExpired = transfer.expires_at && new Date(transfer.expires_at) < new Date();
                      const statusColor = transfer.status === 'pending'
                        ? (isExpired ? 'warning' : 'info')
                        : transfer.status === 'claimed'
                          ? 'success'
                          : transfer.status === 'cancelled'
                            ? 'default'
                            : 'error';

                      return (
                        <Box
                          key={transfer.id}
                          sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1.5, sm: 2 },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            '&:first-of-type': { borderTopLeftRadius: 12, borderTopRightRadius: 12 },
                            '&:last-of-type': { borderBottom: 'none', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 },
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
                            <Box sx={{ minWidth: { xs: 70, sm: 90 } }}>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                ${parseFloat(transfer.amount || 0).toFixed(2)}
                              </Typography>
                              {transfer.currency && (
                                <Typography variant="caption" color="text.disabled">
                                  {transfer.currency}
                                </Typography>
                              )}
                            </Box>

                            <Chip
                              label={isExpired && transfer.status === 'pending' ? 'Expired' : transfer.status}
                              color={statusColor}
                              size="small"
                              sx={{ textTransform: 'capitalize', minWidth: { xs: 64, sm: 80 }, fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                            />
                          </Box>

                          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                          <Divider sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }} />

                          <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
                              {transfer.recipient_email && (
                                <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                                  <Email sx={{ fontSize: { xs: 16, sm: 20 } }} color="action" />
                                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {transfer.recipient_email}
                                  </Typography>
                                </Box>
                              )}
                              {transfer.recipient_phone && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Phone sx={{ fontSize: { xs: 16, sm: 20 } }} color="action" />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {transfer.recipient_phone}
                                  </Typography>
                                </Box>
                              )}
                              {transfer.recipient_name && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Person sx={{ fontSize: { xs: 16, sm: 20 } }} color="action" />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {transfer.recipient_name}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                Created: {new Date(transfer.created_at).toLocaleDateString()}
                              </Typography>
                              {transfer.expires_at && (
                                <Typography variant="caption" color={isExpired ? 'error.main' : 'text.disabled'} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                  Expires: {new Date(transfer.expires_at).toLocaleDateString()}
                                </Typography>
                              )}
                              {transfer.note && (
                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', fontSize: { xs: '0.65rem', sm: '0.75rem' } }} noWrap>
                                  {transfer.note}
                                </Typography>
                              )}
                              {transfer.claim_token && (
                                <Chip label="Claim Link" size="small" variant="outlined" color="info" sx={{ height: 20, fontSize: '0.65rem' }} />
                              )}
                            </Stack>
                          </Box>

                          {transfer.status === 'pending' && !isExpired && (
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, flex: { xs: 1, sm: 'none' } }}
                                onClick={() => {
                                  setSelectedTransfer(transfer);
                                  setEditForm({
                                    expires_in_days: '',
                                    recipient_email: transfer.recipient_email || '',
                                    recipient_phone: transfer.recipient_phone || '',
                                  });
                                  setEditDialogOpen(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, flex: { xs: 1, sm: 'none' } }}
                                onClick={() => {
                                  setSelectedTransfer(transfer);
                                  setCancelDialogOpen(true);
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </>
              ) : (
                <Box
                  textAlign="center"
                  py={8}
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 3,
                    bgcolor: 'grey.50',
                  }}
                >
                  <SwapHoriz sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No {transferStatusFilter === 'pending' ? 'Pending ' : ''}Transfers
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    {transferStatusFilter === 'pending'
                      ? 'You have no pending external transfers at this time.'
                      : `No transfers with status "${transferStatusFilter}" found.`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>
        </TabPanel>
      </Container>

      {/* Cancel Transfer Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => { setCancelDialogOpen(false); setSelectedTransfer(null); }}
      >
        <DialogTitle>Cancel Transfer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this transfer of <strong>${parseFloat(selectedTransfer?.amount || 0).toFixed(2)}</strong>?
            The funds will be refunded to your balance.
          </DialogContentText>
          {selectedTransfer?.recipient_email && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Recipient: {selectedTransfer.recipient_email}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialogOpen(false); setSelectedTransfer(null); }}>
            Keep Transfer
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelTransferMutation.isLoading}
            onClick={() => {
              if (selectedTransfer) {
                cancelTransferMutation.mutate(selectedTransfer.id);
              }
            }}
          >
            {cancelTransferMutation.isLoading ? 'Cancelling...' : 'Cancel & Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Transfer Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSelectedTransfer(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Transfer</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Update the transfer details below. Changing the recipient email or phone will resend the claim link to the new contact.
          </DialogContentText>
          <Stack spacing={3}>
            <TextField
              label="Extend Expiry (days)"
              type="number"
              value={editForm.expires_in_days}
              onChange={(e) => setEditForm((prev) => ({ ...prev, expires_in_days: e.target.value }))}
              fullWidth
              helperText="1–365 days from now"
              inputProps={{ min: 1, max: 365 }}
            />
            <TextField
              label="Recipient Email"
              type="email"
              value={editForm.recipient_email}
              onChange={(e) => setEditForm((prev) => ({ ...prev, recipient_email: e.target.value }))}
              fullWidth
              helperText="Updating email will send the claim link to the new address"
            />
            <TextField
              label="Recipient Phone"
              value={editForm.recipient_phone}
              onChange={(e) => setEditForm((prev) => ({ ...prev, recipient_phone: e.target.value }))}
              fullWidth
              helperText="Updating phone will send the claim SMS to the new number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedTransfer(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={updateTransferMutation.isLoading}
            onClick={() => {
              if (selectedTransfer) {
                const payload = {};
                if (editForm.expires_in_days) payload.expires_in_days = parseInt(editForm.expires_in_days, 10);
                if (editForm.recipient_email && editForm.recipient_email !== selectedTransfer.recipient_email) {
                  payload.recipient_email = editForm.recipient_email;
                }
                if (editForm.recipient_phone && editForm.recipient_phone !== selectedTransfer.recipient_phone) {
                  payload.recipient_phone = editForm.recipient_phone;
                }
                if (Object.keys(payload).length > 0) {
                  updateTransferMutation.mutate({ id: selectedTransfer.id, data: payload });
                } else {
                  setSnackbarMessage("No changes to save");
                  setSnackbarSeverity('info');
                  setSnackbarOpen(true);
                  setEditDialogOpen(false);
                }
              }
            }}
          >
            {updateTransferMutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        fullScreen 
        open={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setIsPreviewOpen(false)}>
              <Cancel />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Bio Page Preview - {title || slug || 'Untitled'}
            </Typography>
          </Toolbar>
        </AppBar>
        <EnhancedBioPagePreview
          title={title}
          slug={slug}
          description={description}
          pageType={pageType}
          viewType={viewType}
          items={items}
          isPublic={isPublic}
          qrEnabled={qrEnabled}
          animationsEnabled={animationsEnabled}
          animationType={animationType}
          linkUserPage={linkUserPage}
          headerMediaUrl={headerMediaUrl}
          linkedContact={linkedContact}
          customtheme={themeObj}
        />
      </Dialog>

      <HelpWizard open={isHelpWizardOpen} onClose={() => setIsHelpWizardOpen(false)} />

      <UploadFileDialog 
        open={isUploadDialogOpen} 
        onClose={() => setIsUploadDialogOpen(false)}
        onUploadSuccess={(data) => {
          if (data?.url) {
            if (uploadTarget === "header") {
              setHeaderMediaUrl(data.url);
            } else if (typeof uploadTarget === "number") {
              handleUpdateItemField(uploadTarget, "media_url", data.url);
            }
          }
          setIsUploadDialogOpen(false);
        }}
      />

      <UploadFileDialog 
        open={profileUploadDialogOpen} 
        onClose={() => setProfileUploadDialogOpen(false)}
        onUploadSuccess={(data) => {
          if (data?.url && profilePhotoCallback) {
            profilePhotoCallback(data.url);
            queryClient.invalidateQueries(['user']);
            queryClient.invalidateQueries(['creator']);
          }
          setProfileUploadDialogOpen(false);
        }}
      />

      <ColorPickerGrid
        open={isBackgroundPickerOpen}
        onClose={() => setIsBackgroundPickerOpen(false)}
        selectedColor={themeObj.backgroundColor || "#FFFFFF"}
        onColorSelect={(color) => setThemeObj((prev) => ({ ...prev, backgroundColor: color }))}
      />

      <ColorPickerGrid
        open={isForegroundPickerOpen}
        onClose={() => setIsForegroundPickerOpen(false)}
        selectedColor={themeObj.foregroundColor || "#000000"}
        onColorSelect={(color) => setThemeObj((prev) => ({ ...prev, foregroundColor: color }))}
      />
    </Box>
  );
}